import { useRouter } from 'next/router'
import { Trans } from 'next-i18next'

import {
  Heading1,
  Heading2,
  Heading3,
  LinkGrid,
  LinkStack,
  LinkText,
  Paragraph,
  Section,
  Section1,
  Section2,
  Wrapper
} from '@/features/Listings/seo/elements'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import PopularCities from '@/features/Listings/seo/search-results-content/content/popular-cities'
import PopularHoods from '@/features/Listings/seo/search-results-content/content/popular-hoods'
import PopularUniversities from '@/features/Listings/seo/search-results-content/content/popular-universities'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'
import { toTitleCase } from '@/features/Listings/seo/search-results-content/content/utils'
import { Label } from '@/components/text'

const ContentTemplate = ({
  content,
  filterSlug,
  cityName,
  cityNameWithState,
  citySlug,
  state,
  neighborhoodName,
  neighbourhoodSlug,
  preventRepeatedCityName = false
}) => {
  const router = useRouter()
  const isChinese = router.locale === 'zh'
  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)
  const { hasFilter, filterTitle, filterName } = useFilterSlug(filterSlug)

  const neighbourhoodWithCityAndState = preventRepeatedCityName
    ? `${neighborhoodName}, ${state}`
    : `${neighborhoodName}, ${cityNameWithState}`

  const neighbourhoodWithCity = preventRepeatedCityName
    ? neighborhoodName
    : `${neighborhoodName}, ${cityName}`

  return (
    <Wrapper>
      <Section>
        <Section1>
          <Section2>
            <Heading1>
              <Trans
                i18nKey="seo:About AREA LOCATION"
                values={{
                  area: neighborhoodName,
                  location: preventRepeatedCityName ? state : cityNameWithState
                }}
                defaults="About {{ area }}, {{ location }}"
              />
            </Heading1>
            {content.about}
          </Section2>
        </Section1>
      </Section>
      <Section>
        <Section1>
          <Section2>
            <Heading1>
              <Trans
                i18nKey="seo:Renting in LOCATION"
                values={{
                  location: neighbourhoodWithCityAndState
                }}
                defaults="Renting in {{ location }}"
              />
            </Heading1>
            {content.rentingIn}
          </Section2>
          <Section2>
            <Heading2>
              <Trans
                i18nKey="seo:Average Rent in LOCATION"
                values={{
                  location: neighbourhoodWithCityAndState
                }}
                defaults="Average Rent in {{ location }}"
              />
            </Heading2>
            {content.averageRent}
          </Section2>
          <Section2>
            <Heading2>
              <Trans
                i18nKey="seo:Apartment Hunting in LOCATION"
                values={{
                  location: neighbourhoodWithCityAndState
                }}
                defaults="Apartment Hunting in {{ location }}"
              />
            </Heading2>
            {content.apartmentHunting}
          </Section2>
          <Section2>
            <Heading2>
              <Trans
                i18nKey="seo:LOCATION Rental Types"
                values={{
                  location: neighbourhoodWithCity
                }}
                defaults="{{ location }} Rental Types"
              />
            </Heading2>
            <Paragraph>
              <Trans i18nKey="seo:Property Rental Types Paragraph">
                There are many rental property types you can choose from in{' '}
                {{ cityName: neighbourhoodWithCity }}.
              </Trans>
            </Paragraph>
            {content.rentalTypes}
          </Section2>
          <Section2>
            <Heading2>
              <Trans
                i18nKey="seo:Special LOCATION Rental Types"
                values={{
                  location: neighbourhoodWithCity
                }}
                defaults="Special {{ location }} Rental Types"
              />
            </Heading2>
            <Paragraph>
              <Trans i18nKey="seo:Special Rental Types Paragraph">
                In addition to choosing your property type, there are also
                specific rental types you can look into.
              </Trans>
            </Paragraph>
            {content.specialTypes}
          </Section2>
          <Section2>
            <Heading2>
              <Trans
                i18nKey="seo:LOCATION Rental Amenities"
                values={{
                  location: neighbourhoodWithCity
                }}
                defaults="{{ location }} Rental Amenities"
              />
            </Heading2>
            {content.amenities}
          </Section2>
        </Section1>
      </Section>
      <Section>
        <Section1>
          {content.exploring && (
            <Section2>
              <Heading1>
                <Trans
                  i18nKey="seo:Exploring LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Exploring {{ location }}"
                />
              </Heading1>
              {content.exploring}
            </Section2>
          )}
          {content.thingsToDo && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Things to do in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Things to do in {{ location }}"
                />
              </Heading2>
              {content.thingsToDo}
            </Section2>
          )}
          {content.restaurants && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Top restaurants in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Top restaurants in {{ location }}"
                />
              </Heading2>
              {content.restaurants}
            </Section2>
          )}
          {content.transportation && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Transportation options in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Transportation options in {{ location }}"
                />
              </Heading2>
              {content.transportation}
            </Section2>
          )}
          {content.whereToStay && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Where to stay in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Where to stay in {{ location }}"
                />
              </Heading2>
              {content.whereToStay}
            </Section2>
          )}
          {content.livingIn && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Living in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Living in {{ location }}"
                />
              </Heading2>
              {content.livingIn}
            </Section2>
          )}
          {content.landmarks && (
            <Section2>
              <Heading2>
                <Trans
                  i18nKey="seo:Local Landmarks in LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Local Landmarks in {{ location }}"
                />
              </Heading2>
              {content.landmarks}
            </Section2>
          )}
          <PopularHoods
            state={state}
            locationName={neighbourhoodWithCityAndState}
            neighbourhoodSlug={neighbourhoodSlug}
          />
          <PopularCities
            state={state}
            citySlug={citySlug}
            location={neighbourhoodWithCityAndState}
          />
          <PopularUniversities
            state={state}
            locationName={neighbourhoodWithCityAndState}
            neighbourhoodSlug={neighbourhoodSlug}
            citySlug={citySlug}
          />
        </Section1>
      </Section>
      <Section>
        <Section1>
          <Section2>
            <Heading1>
              {hasFilter ? (
                <Trans
                  i18nKey="seo:FILTER LOCATION"
                  values={{
                    filter: toTitleCase(filterTitle),
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="{{ filter }} {{ location }}"
                />
              ) : (
                <Trans
                  i18nKey="seo:Apartments for Rent LOCATION"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Apartments for Rent {{ location }}"
                />
              )}
            </Heading1>
            <Paragraph>
              <Trans
                i18nKey="seo:Apartments for Rent near LOCATION default Paragraph"
                values={{
                  location: neighbourhoodWithCity
                }}
                defaults="Search for condos, townhouses, rooms, and houses for rent near {{ location }} on liv.rent. Finding apartments for rent near you has never been easier. Filter your search by price range (great for finding cheap apartments for rent), unfurnished or furnished apartments for rent, pet-friendly rentals, availability, and housing type. Options range from studio apartments and suites for rent to 1 or 2 bedroom apartments and 3+ bedrooms for rent."
              />
            </Paragraph>
            <Paragraph>
              <Trans
                i18nKey="seo:b.listing.view.our_rental_listings_date.title"
                values={{
                  cityName: neighbourhoodWithCityAndState,
                  currentDate: new Intl.DateTimeFormat(
                    isChinese ? 'zh-CN' : 'en-US',
                    {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }
                  ).format(new Date())
                }}
                defaults="Our {{ cityName }} rental listings are updated in real-time and are current as of {{ currentDate }}."
              />
            </Paragraph>
          </Section2>
        </Section1>
      </Section>
      <Section>
        <Section1>
          <Section2>
            <Heading1>
              {hasFilter ? (
                <Trans
                  i18nKey="seo:Looking for FILTER in LOCATION?"
                  values={{
                    filter: toTitleCase(filterName),
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Looking for {{ filter }} in {{ location }}?"
                />
              ) : (
                <Trans
                  i18nKey="seo:Looking for Apartments in LOCATION?"
                  values={{
                    location: neighbourhoodWithCityAndState
                  }}
                  defaults="Looking for Apartments in {{ location }}?"
                />
              )}
            </Heading1>
            <Paragraph>
              <Trans
                i18nKey="seo:Looking for Apartments in LOCATION? Paragraph"
                values={{
                  location: neighbourhoodWithCityAndState
                }}
                defaults="Looking for a specific type of rental in {{ location }}? Use our filters below to find your next home."
              />
            </Paragraph>
          </Section2>
          <LinkGrid>
            <LinkStack>
              <Heading3>
                <Trans
                  i18nKey="seo:LOCATION Rental Types"
                  values={{
                    location: neighbourhoodWithCity
                  }}
                  defaults="{{ location }} Rental Types"
                />
              </Heading3>
              <Label fontSize="14px">
                <LinkText href={links.apartment}>
                  <Trans
                    i18nKey="seo:Apartments for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Apartments for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.apartment}>
                  <Trans
                    i18nKey="seo:Condos for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Condos for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.townhouse}>
                  <Trans
                    i18nKey="seo:Townhouses for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Townhouses for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.house}>
                  <Trans
                    i18nKey="seo:Houses for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Houses for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.room}>
                  <Trans
                    i18nKey="seo:Rooms for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Rooms for rent {{ location }}"
                  />
                </LinkText>
              </Label>
            </LinkStack>
            <LinkStack>
              <Heading3>
                <Trans
                  i18nKey="seo:LOCATION Rentals by Bedrooms"
                  values={{
                    location: neighbourhoodWithCity
                  }}
                  defaults="{{ location }} Rentals by Bedrooms"
                />
              </Heading3>
              <Label fontSize="14px">
                <LinkText href={links.studio}>
                  <Trans
                    i18nKey="seo:Studios for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Studios for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.bed1}>
                  <Trans
                    i18nKey="seo:1 bedrooms for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="1 bedrooms for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.bed2}>
                  <Trans
                    i18nKey="seo:2 bedrooms for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="2 bedrooms for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.bed3}>
                  <Trans
                    i18nKey="seo:3 bedrooms for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="3 bedrooms for rent {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.bed4}>
                  <Trans
                    i18nKey="seo:4+ bedrooms for rent LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="4+ bedrooms for rent {{ location }}"
                  />
                </LinkText>
              </Label>
            </LinkStack>
            <LinkStack>
              <Heading3>
                <Trans
                  i18nKey="seo:LOCATION Rentals by Amenity"
                  values={{
                    location: neighbourhoodWithCity
                  }}
                  defaults="{{ location }} Rentals by Amenity"
                />
              </Heading3>
              <Label fontSize="14px">
                <LinkText href={links.furnished}>
                  <Trans
                    i18nKey="seo:Furnished rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Furnished rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.unfurnished}>
                  <Trans
                    i18nKey="seo:Unfurnished rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Unfurnished rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.petFriendly}>
                  <Trans
                    i18nKey="seo:Pet-friendly rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Pet-friendly rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.dogFriendly}>
                  <Trans
                    i18nKey="seo:Dog-friendly rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Dog-friendly rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.catFriendly}>
                  <Trans
                    i18nKey="seo:Cat-friendly rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Cat-friendly rentals {{ location }}"
                  />
                </LinkText>
              </Label>
            </LinkStack>
            <LinkStack>
              <Heading3>
                <Trans
                  i18nKey="seo:LOCATION Rentals by Lease"
                  values={{
                    location: neighbourhoodWithCity
                  }}
                  defaults="{{ location }} Rentals by Lease"
                />
              </Heading3>
              <Label fontSize="14px">
                <LinkText href={links.longTerm}>
                  <Trans
                    i18nKey="seo:Long term rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Long term rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.shortTerm}>
                  <Trans
                    i18nKey="seo:Short term rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Short term rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.monthToMonth}>
                  <Trans
                    i18nKey="seo:Month-to-month rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Month-to-month rentals {{ location }}"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.fixedTerm}>
                  <Trans
                    i18nKey="seo:Fixed term rentals LOCATION"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="Fixed term rentals {{ location }}"
                  />
                </LinkText>
              </Label>
            </LinkStack>
            <LinkStack>
              <Heading3>
                <Trans
                  i18nKey="seo:LOCATION Rentals by Price"
                  values={{
                    location: neighbourhoodWithCity
                  }}
                  defaults="{{ location }} Rentals by Price"
                />
              </Heading3>
              <Label fontSize="14px">
                <LinkText href={links.under1000}>
                  <Trans
                    i18nKey="seo:LOCATION apartments under $1,000"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="{{ location }} apartments under $1,000"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.under1500}>
                  <Trans
                    i18nKey="seo:LOCATION apartments under $1,500"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="{{ location }} apartments under $1,500"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.under2000}>
                  <Trans
                    i18nKey="seo:LOCATION apartments under $2,000"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="{{ location }} apartments under $2,000"
                  />
                </LinkText>
              </Label>
              <Label fontSize="14px">
                <LinkText href={links.under2500}>
                  <Trans
                    i18nKey="seo:LOCATION apartments under $2,500"
                    values={{
                      location: neighbourhoodWithCity
                    }}
                    defaults="{{ location }} apartments under $2,500"
                  />
                </LinkText>
              </Label>
            </LinkStack>
          </LinkGrid>
        </Section1>
      </Section>
    </Wrapper>
  )
}

export default ContentTemplate
